import { Separator } from "@/components/ui/separator";
import malaysianFlag from "@/assets/icons/malaysia.svg";
import globalFlag from "@/assets/icons/global.svg";
import localFlag from "@/assets/icons/local.svg";
import { formatCurrency } from "@/utils/formatCurrency";
import { ArrowRight } from "lucide-react";
import { topupRouteTitle } from "@/lib/topup";
import type { Route } from "@/types/topup/summary";

// Malaysian first, then Foreign, then any other category last.
const CATEGORY_ORDER = ["Malaysian", "Foreign"];

const sortByCategoryAndTicketType = (ticketList: Route["ticketList"]) =>
  [...ticketList].sort((a, b) => {
    const rank = (category: string) => {
      const index = CATEGORY_ORDER.indexOf(category);
      return index === -1 ? CATEGORY_ORDER.length : index;
    };

    const categoryOrder =
      rank(a.ticketTypeCategoryName) - rank(b.ticketTypeCategoryName);

    if (categoryOrder !== 0) {
      return categoryOrder;
    }

    return a.ticketTypeName.localeCompare(b.ticketTypeName);
  });

type TopupBreakdownProps = {
  route: Route;
};

export function TopupBreakDown({ route }: TopupBreakdownProps) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="flex flex-wrap items-center gap-1 text-sm font-semibold text-primary">
        {topupRouteTitle(route.routeGroup)
          .split(" - ")
          .map((part, index) => (
            <span key={index} className="flex items-center gap-1">
              {index > 0 && (
                <ArrowRight size={14} className="shrink-0" aria-hidden="true" />
              )}

              {part}
            </span>
          ))}
      </h3>

      <div className="overflow-hidden rounded-2xl bg-card shadow-xl">
        {/* Header */}
        <div className="grid h-10 grid-cols-[1.5fr_auto_1fr] items-center gap-2 bg-primary px-4 py-2 text-xs font-medium text-primary-foreground">
          <span>Passenger Type</span>
          <span className="text-center">Qty</span>
          <span className="text-right">Price</span>
        </div>

        {/* Tickets */}
        <div className="flex flex-col gap-4 px-4 pt-2 pb-4">
          {sortByCategoryAndTicketType(route.ticketList).map(
            (ticket, index) => (
              <div
                key={`${ticket.ticketTypeName}-${index}`}
                className="grid grid-cols-[1.5fr_auto_1fr] items-center gap-2 text-xs font-medium text-secondary-foreground"
              >
                <span className="flex items-center gap-3">
                  <img
                    src={
                      ticket.ticketTypeCategoryName === "Malaysian"
                        ? malaysianFlag
                        : ticket.ticketTypeCategoryName === "Local"
                          ? localFlag
                          : globalFlag
                    }
                    alt={
                      ticket.ticketTypeCategoryName === "Malaysian"
                        ? "Malaysian Flag"
                        : ticket.ticketTypeCategoryName === "Local"
                          ? "Local Flag"
                          : "Global Flag"
                    }
                    className="h-4 w-auto"
                  />
                  {ticket.ticketTypeName} ({ticket.ticketTypeCategoryName})
                </span>

                <span className="text-center">{ticket.quantity}</span>

                <span className="text-right">
                  {formatCurrency(ticket.price)}
                </span>
              </div>
            ),
          )}

          <Separator />

          {/* Total */}
          <div className="flex items-center justify-between text-sm font-medium text-secondary-foreground">
            <span>Total</span>

            <span>
              {formatCurrency(
                route.ticketList.reduce((sum, ticket) => sum + ticket.price, 0),
              )}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
